import React, { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { PromiseState } from 'react-refetch';

import Box from 'components/elements/Box';
import P from 'components/elements/p';
import SearchField from 'components/styled/SearchField/SearchField';
import { NoResults } from 'components/utility/NoResults/NoResults';
import AddToListButton from 'components/base/AddToListButton';
import {
  ResultItemWrapper,
  StyledEntityItem,
} from 'components/search/ResultsSection/ResultsList/ResultsList';
import SearchResultsLoader from 'components/utility/ContentLoader/SearchResultsLoader/SearchResultsLoader';
import {
  CaretVersions,
  VersionsButton,
} from 'components/TopicFilters/ViewRelatedMenu/ViewRelatedMenu.styles';
import { ButtonBack } from 'components/TopicFilters/ViewRelatedMenu/ViewRelatedMenu.styles';

import { ReactComponent as IconSearch } from 'icons/icon-search.svg';
import { ReactComponent as CaretBack } from 'icons/icon-toggle-caret.svg';

import colors from 'styles/themes/colors';

import { Label, ScrollBox, SearchDropdownBox } from '../styles';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

const noop = (): void => {}; // eslint-disable-line @typescript-eslint/no-empty-function

const InitialView = (): JSX.Element => (
  <Box my="4rem" textAlign="center" color={colors.MediumGrey}>
    <IconSearch />
    <P fontSize="1.25rem">Monitor Playlist Activity</P>
    <P fontSize="0.875rem">Search any track to monitor playlist activity</P>
  </Box>
);

const EntityList: React.FC<{
  foundEntities: any;
  addedEntities: { id: string; type: 'track' }[];
  addEntityToCustomList: any;
  isVersion?: boolean;
  setShowTracks: any;
  showVersions: any;
  addingTrack?: string;
  deleteTrack?: Function;
  deletingTrack?: string;
}> = ({
  foundEntities,
  addedEntities,
  addEntityToCustomList,
  isVersion,
  setShowTracks,
  showVersions,
  addingTrack,
  deleteTrack,
  deletingTrack,
}) => {
  const watchTrack = (trackId: any, isAdded: boolean): void => {
    if (isAdded) {
      deleteTrack && deleteTrack(trackId);
    } else {
      addEntityToCustomList(trackId);
    }
    logToAmplitude('Playlist Activity -> Add A Track -> Watch button clicked');
  };
  return (
    <ScrollBox
      display="flex"
      flexDirection="column"
      overflowY="scroll"
      overflowX="hidden"
      maxHeight="404px"
      width="100%"
    >
      {foundEntities?.map((originalTrack: any, index: number) => {
        const track = isVersion ? originalTrack.track : originalTrack;
        const addedTrack = addedEntities?.find(
          (item: { id: string; type: 'track' }) => track.id === item.id
        );
        const isAdded = !!addedTrack;
        const trackAdding = addingTrack === track.id;
        const trackDeleting = deletingTrack === track.id;

        return (
          <ResultItemWrapper display="flex" alignItems="center" key={index}>
            <StyledEntityItem
              entityId={track.id}
              entityType={track.type}
              displayName={track.displayName}
              primaryArtist={track.primaryArtist}
              thumbnailUrl={track.thumbnailUrl}
              releaseDate={track.releaseDate}
              isGroup={track.is_group}
              parentEntityId={track.parent_group_id}
              groupChildCount={track.group_child_count}
              releaseCount={track.grouped_album_count}
              artistCount={track.artist_count}
              trackCount={track.grouped_track_count}
              entityData={track.data}
              artworkHasBackground={true}
              isLink={false}
              preserveSearch={true}
              releaseType={track?.productType ? track?.productType : null}
              isrc_list={track?.isrc_list}
            />
            {track?.group_child_count > 1 && (
              <VersionsButton
                marginRight="10px"
                onClick={(): any => {
                  showVersions({ id: track.id });
                  setShowTracks(false);
                }}
              >
                {track.group_child_count} versions <CaretVersions />
              </VersionsButton>
            )}
            <Box marginRight="4px">
              <AddToListButton
                onClick={(): any =>
                  (!trackAdding || !trackDeleting) &&
                  watchTrack(track.id, isAdded)
                }
                icon={isAdded ? 'check' : 'plus'}
                loading={trackAdding || trackDeleting}
                forward={!isAdded}
              />
            </Box>
          </ResultItemWrapper>
        );
      })}
    </ScrollBox>
  );
};

type PropsType = {
  addTrackToCustomList: (entityId: string) => void;
  isSmallScreen: boolean;
  setSearchQuery: Function;
  foundTracks: PromiseState<any[]>;
  addedTracks: { id: string; type: 'track' }[];
  showVersions: any;
  versions: any;
  isSeparate?: boolean;
  addingTrack?: string;
  deleteTrack?: Function;
  deletingTrack?: string;
};

const SearchDropdown: React.FC<PropsType> = ({
  addTrackToCustomList,
  isSmallScreen,
  setSearchQuery,
  foundTracks,
  addedTracks,
  showVersions,
  versions,
  isSeparate,
  addingTrack,
  deleteTrack = noop,
  deletingTrack,
}) => {
  const { fulfilled, pending, value } = foundTracks;
  const {
    fulfilled: versionsFulfilled,
    pending: versionsPending,
    value: versionsValue,
  } = versions;

  const hasNoSearchResults = fulfilled && value && value.length === 0;
  const trackVersions = useMemo(
    () =>
      versionsValue?.tracks && versionsValue?.tracks?.length > 0
        ? versionsValue?.tracks
        : [],
    [versionsValue?.tracks]
  );

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const searchRef = useRef<HTMLInputElement>(null);
  const [showTracks, setShowTracks] = useState(true);

  const doSearchQuery = useCallback(
    debounce((str: string) => setSearchQuery(str), 250),
    []
  );

  const handleSearchInput = useCallback(
    (event) => {
      const value = event.target.value;
      setSearchInputValue(value);
      doSearchQuery(value);
    },
    [doSearchQuery]
  );

  const onClickBack = (): void => setShowTracks(true);

  return (
    <SearchDropdownBox
      width={isSmallScreen ? '100%' : '560px'}
      padding={isSmallScreen ? '0 0.75rem' : 5}
      isSeparate={isSeparate}
      isSmallScreen={isSmallScreen}
      id="trackSearchDropdown"
    >
      <SearchField
        refObj={searchRef}
        handleSearch={handleSearchInput}
        value={searchInputValue}
        placeholder="Search a track or ISRC"
        focusOnMount
      />
      {pending && searchInputValue && <SearchResultsLoader />}

      {showTracks ? (
        <>
          {fulfilled && searchInputValue && value && value?.length > 0 && (
            <Box>
              <Box display="flex" justifyContent="space-between">
                <Label>Track name</Label>
                <Label>Watch</Label>
              </Box>

              <EntityList
                foundEntities={foundTracks?.value}
                addedEntities={addedTracks}
                addEntityToCustomList={addTrackToCustomList}
                setShowTracks={setShowTracks}
                showVersions={showVersions}
                addingTrack={addingTrack}
                deleteTrack={deleteTrack}
                deletingTrack={deletingTrack}
              />
            </Box>
          )}
          {!searchInputValue && <InitialView />}

          {hasNoSearchResults && <NoResults searchValue={searchInputValue} />}
        </>
      ) : (
        <>
          {versionsPending && <SearchResultsLoader />}

          {versionsFulfilled && trackVersions && trackVersions?.length > 0 && (
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <ButtonBack onClick={onClickBack}>
                <CaretBack />
                &nbsp;&nbsp;Back
              </ButtonBack>

              <Label>Versions</Label>

              <EntityList
                foundEntities={trackVersions}
                addedEntities={addedTracks}
                addEntityToCustomList={addTrackToCustomList}
                setShowTracks={setShowTracks}
                showVersions={showVersions}
                addingTrack={addingTrack}
                isVersion
              />
            </Box>
          )}
        </>
      )}
    </SearchDropdownBox>
  );
};

export default SearchDropdown;
